import React, { useCallback, useEffect, useState } from "react";
import { T, GOLD_INK } from "../theme/tokens.js";
import { Panel, Btn, Chip, Notice, Modal, Field, Labelled, Empty } from "../components/ui/index.jsx";
import { supabase, friendlyError } from "../lib/supabase.js";
import { truncate } from "../lib/format.js";

/* Assignments are the access control. A checkbox here decides what the
   database will hand that person, not what the screen chooses to draw. */
export default function Team({ contracts, isAdmin, isOwner, me }) {
  const [people, setPeople] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [inviting, setInviting] = useState(false);
  const [issued, setIssued] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [working, setWorking] = useState("");

  const load = useCallback(async () => {
    const [{ data: profiles, error: pErr }, { data: members, error: mErr }] = await Promise.all([
      supabase.from("profiles").select("id, email, full_name, role, is_owner, deactivated_at").order("full_name"),
      supabase.from("contract_members").select("contract_id, user_id"),
    ]);
    if (pErr || mErr) setError(friendlyError(pErr || mErr));
    else {
      setPeople(profiles ?? []);
      setAssignments(members ?? []);
      setError("");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /* Your own role is not yours to change, and the owner's is nobody's. Both
     are refused by the database as well; this only keeps the screen honest
     about what will be accepted. A removed person keeps the role they had,
     because restoring them should put back exactly what was there. */
  const canSetRole = (u) =>
    isAdmin && !u.is_owner && u.id !== me?.id && !u.deactivated_at;

  const assigned = (userId, contractId) =>
    assignments.some((a) => a.user_id === userId && a.contract_id === contractId);

  const toggle = async (userId, contractId) => {
    if (!isAdmin) return;
    setSaving(`${userId}:${contractId}`);
    const on = assigned(userId, contractId);
    const { error } = on
      ? await supabase.from("contract_members").delete()
          .eq("user_id", userId).eq("contract_id", contractId)
      : await supabase.from("contract_members").insert({ user_id: userId, contract_id: contractId });
    if (error) setError(friendlyError(error));
    else { setError(""); await load(); }
    setSaving("");
  };

  /* Changing a role goes straight at the database, unlike inviting and
     removing. It needs no privileged key: an admin is allowed to do it, the
     guard trigger refuses the cases that would be dangerous, and the browser
     is told no by Postgres rather than by this screen. */
  const changeRole = async (user, role) => {
    if (role === user.role) return;
    setSaving(`role:${user.id}`);
    const { error } = await supabase.from("profiles").update({ role }).eq("id", user.id);
    if (error) setError(friendlyError(error));
    else { setError(""); await load(); }
    setSaving("");
  };

  /* Removing somebody takes the service role key, which lives on the server
     and never reaches a browser, so this goes through a function rather than
     straight at the database. */
  const setActive = async (user, active) => {
    setWorking(user.id);
    setError("");
    const { data: session } = await supabase.auth.getSession();
    try {
      const res = await fetch("/.netlify/functions/set-user-active", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ userId: user.id, active }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.error) setError(body.error || "That could not be done. Try again.");
      await load();
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    }
    setWorking("");
    setConfirmRemove(null);
  };

  /* Issuing a fresh password, for somebody locked out. The self-service reset
     link still exists on the sign-in screen; this is the fallback for when a
     company spam filter eats it, which is a real possibility rather than a
     theoretical one. */
  const reissue = async (user) => {
    setWorking(user.id);
    setError("");
    const { data: session } = await supabase.auth.getSession();
    try {
      const res = await fetch("/.netlify/functions/reissue-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ userId: user.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) setError(body.error || "That could not be done. Try again.");
      else {
        if (body.error) setError(body.error);
        setIssued({ name: body.name, password: body.password, again: true });
      }
      await load();
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    }
    setWorking("");
  };

  /* Deleting outright, for an account that was made to test something and
     never used. The database refuses it if the person has written anything,
     which is the check that matters and the one nobody has to maintain. */
  const deleteAccount = async (user) => {
    setWorking(user.id);
    setError("");
    const { data: session } = await supabase.auth.getSession();
    try {
      const res = await fetch("/.netlify/functions/delete-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ userId: user.id }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body.error) setError(body.error || "That could not be done. Try again.");
      await load();
    } catch {
      setError("Could not reach the server. Check your connection and try again.");
    }
    setWorking("");
    setConfirmDelete(null);
  };

  /* A failed load used to leave this spinning forever: the error was set and
     the early return below showed "Loading..." before anything could render
     it, so a broken page and a slow one looked identical. Say which. */
  if (people === null) {
    return (
      <Panel title="Team">
        {error ? <Notice kind="error">{error}</Notice> : <Empty text="Loading..." />}
      </Panel>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16, maxWidth: 720 }}>
      {error && <Notice kind="error">{error}</Notice>}

      {confirmRemove && (
        <Modal title={`Remove ${confirmRemove.full_name || confirmRemove.email}?`}
          onClose={() => setConfirmRemove(null)}>
          <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.75 }}>
            They will not be able to sign in, and they will reach no contract,
            no register and no report.
            <br /><br />
            Nothing they wrote is deleted. Their name stays on every row they
            added or changed, because a person leaving the program is not a
            reason for the history to stop being true. Their contract
            assignments are kept too, so you can restore them later exactly as
            they were.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn kind="ghost" onClick={() => setConfirmRemove(null)}>Cancel</Btn>
            <Btn kind="danger" disabled={working === confirmRemove.id}
              onClick={() => setActive(confirmRemove, false)}>
              {working === confirmRemove.id ? "Removing..." : "Remove them"}
            </Btn>
          </div>
          <div style={{
            marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.line}`,
            fontSize: 12.5, color: T.soft, lineHeight: 1.7,
          }}>
            If this account was made to try something and never used, there is no
            history to keep and leaving it here says somebody has access who does
            not.{" "}
            <button
              type="button"
              onClick={() => { setConfirmDelete(confirmRemove); setConfirmRemove(null); }}
              style={{
                background: "none", border: "none", padding: 0, font: "inherit",
                color: T.risk, textDecoration: "underline", cursor: "pointer",
              }}
            >Delete the account entirely</button>
            {" "}instead.
          </div>
        </Modal>
      )}

      {confirmDelete && (
        <Modal title={`Delete ${confirmDelete.full_name || confirmDelete.email}?`}
          onClose={() => setConfirmDelete(null)}>
          <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.75 }}>
            This erases the account. They disappear from this page, their sign-in
            is deleted, and nothing about them is kept.
            <br /><br />
            It only works if they have never written anything. If their name is on
            a single row in any register, the database refuses and tells you so,
            because that history belongs to whoever is relying on it rather than
            to the person who left. Remove is the answer in that case.
            <br /><br />
            <b>This cannot be undone.</b>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn kind="ghost" onClick={() => setConfirmDelete(null)}>Cancel</Btn>
            <Btn kind="danger" disabled={working === confirmDelete.id}
              onClick={() => deleteAccount(confirmDelete)}>
              {working === confirmDelete.id ? "Deleting..." : "Delete the account"}
            </Btn>
          </div>
        </Modal>
      )}

      <Panel
        title="Team members"
        accent={T.gold}
        right={isOwner ? <Btn small kind="gold" onClick={() => setInviting(true)}>Add someone</Btn> : null}
      >
        {people.map((u) => (
          <div key={u.id} style={{
            padding: "12px 0", borderBottom: `1px solid ${T.line}`,
            opacity: u.deactivated_at ? 0.55 : 1,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div>
                <b style={{ fontSize: 14.5,
                            textDecoration: u.deactivated_at ? "line-through" : "none" }}>
                  {u.full_name || u.email}
                </b>{" "}
                <span style={{ fontSize: 12, color: T.soft }}>{u.email}</span>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {u.deactivated_at && <Chip color={T.risk}>Removed</Chip>}
                {u.is_owner && <Chip color={GOLD_INK}>Owner</Chip>}
                {canSetRole(u) ? (
                  <select
                    aria-label={`Role for ${u.full_name || u.email}`}
                    value={u.role}
                    disabled={saving === `role:${u.id}`}
                    onChange={(e) => changeRole(u, e.target.value)}
                    style={{
                      padding: "3px 6px", border: `1px solid ${T.line}`, borderRadius: 4,
                      fontSize: 12.5, background: "#fff", color: T.ink,
                      opacity: saving === `role:${u.id}` ? 0.5 : 1,
                    }}
                  >
                    <option value="viewer">viewer</option>
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                  </select>
                ) : (
                  <Chip color={u.role === "admin" ? T.navy : u.role === "viewer" ? T.soft : T.steel}>
                    {u.role}
                  </Chip>
                )}
                {isOwner && !u.is_owner && u.id !== me?.id && (
                  u.deactivated_at ? (
                    <>
                      <Btn small kind="ghost" disabled={working === u.id}
                        onClick={() => setActive(u, true)}>
                        {working === u.id ? "..." : "Restore"}
                      </Btn>
                      <Btn small kind="danger" disabled={working === u.id}
                        onClick={() => setConfirmDelete(u)}>Delete</Btn>
                    </>
                  ) : (
                    <>
                      <Btn small kind="ghost" disabled={working === u.id}
                        onClick={() => reissue(u)}>
                        {working === u.id ? "..." : "New password"}
                      </Btn>
                      <Btn small kind="danger" disabled={working === u.id}
                        onClick={() => setConfirmRemove(u)}>Remove</Btn>
                    </>
                  )
                )}
              </div>
            </div>

            {u.deactivated_at ? (
              <div style={{ fontSize: 12, color: T.soft, marginTop: 6, lineHeight: 1.6 }}>
                Removed from the workspace. They cannot sign in and reach nothing,
                and their name stays on everything they wrote. Their contracts are
                kept, so Restore puts them back exactly as they were.
                <br />
                <b style={{ color: T.steel }}>Only you can see this row.</b> Who was
                removed is not something an admin is shown.
              </div>
            ) : u.role === "admin" ? (
              <div style={{ fontSize: 12, color: T.soft, marginTop: 6 }}>Sees every contract</div>
            ) : (
              <div style={{ marginTop: 8 }}>
                <div style={{
                  fontSize: 11.5, fontWeight: 700, letterSpacing: "0.08em",
                  textTransform: "uppercase", color: GOLD_INK, marginBottom: 5,
                }}>Assigned contracts</div>
                {contracts.length === 0 ? (
                  <span style={{ fontSize: 12, color: T.soft }}>No contracts exist yet.</span>
                ) : (
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {contracts.map((c) => (
                      <label key={c.id} style={{
                        fontSize: 13, color: T.ink, display: "flex", alignItems: "center",
                        gap: 5, cursor: isAdmin ? "pointer" : "default",
                        opacity: saving === `${u.id}:${c.id}` ? 0.5 : 1,
                      }}>
                        <input
                          type="checkbox"
                          disabled={!isAdmin || saving === `${u.id}:${c.id}`}
                          checked={assigned(u.id, c.id)}
                          onChange={() => toggle(u.id, c.id)}
                        />
                        {truncate(c.name, 30)}
                      </label>
                    ))}
                  </div>
                )}
                {contracts.length > 0 && !contracts.some((c) => assigned(u.id, c.id)) && (
                  <div style={{ fontSize: 12, color: T.warn, marginTop: 5 }}>
                    No contracts assigned, so this member signs in to an empty workspace.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {!isAdmin && (
          <div style={{ marginTop: 12, fontSize: 13, color: T.soft }}>
            Ask Heidi to change assignments or add members.
          </div>
        )}
      </Panel>

      <Notice>
        Assignments are enforced by the database, not by this screen. A member who is
        not assigned to a contract receives nothing for it even if they go looking
        outside the app.
        {isAdmin && (
          <>
            <br /><br />
            <b>Viewer</b> reads the contracts you tick and changes nothing.{" "}
            <b>Member</b> reads and edits the contracts you tick.{" "}
            <b>Admin</b> reads and edits every contract without being ticked, sees the
            portfolio view, and can change other people's roles. Your own role and
            Heidi's cannot be changed here.
          </>
        )}
      </Notice>

      {inviting && (
        <InviteModal
          onClose={() => setInviting(false)}
          onDone={load}
          onIssued={setIssued}
        />
      )}

      {issued && <PasswordModal issued={issued} onClose={() => setIssued(null)} />}
    </div>
  );
}

function InviteModal({ onClose, onDone, onIssued }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  /* Creating an account is a privileged operation, which the browser key is
     not allowed to do. The request goes to a server-side function that checks
     the caller is the owner before it uses the privileged key.

     No email is sent. The app hands back a temporary password and Heidi passes
     it on herself. An invitation link sitting in an inbox is a working key in
     a place nobody here controls; a password that only opens the screen asking
     for a real one is a smaller thing to hand over. */
  const send = async () => {
    setBusy(true);
    setError("");
    const { data: { session } } = await supabase.auth.getSession();
    try {
      const res = await fetch("/.netlify/functions/add-user", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({ email: email.trim().toLowerCase(), fullName: fullName.trim(), role }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) setError(body.error || "The account could not be created.");
      else {
        onDone();
        onIssued({ name: fullName.trim() || body.email, email: body.email, password: body.password });
        onClose();
      }
    } catch {
      setError("Could not reach the server. Try again in a moment.");
    }
    setBusy(false);
  };

  return (
    <Modal title="Add someone" onClose={onClose}>
      <div style={{ display: "grid", gap: 12 }}>
        <Labelled label="Work email" hint="Must be a wittobriens.com address.">
          <Field f={{ type: "email" }} value={email} onChange={setEmail} autoFocus
            placeholder="name@wittobriens.com" />
        </Labelled>
        <Labelled label="Full name">
          <Field f={{ type: "text" }} value={fullName} onChange={setFullName} />
        </Labelled>
        <Labelled label="Role">
          <Field f={{ type: "select", options: ["viewer", "member", "admin"], req: true }}
            value={role} onChange={setRole} />
          <div style={{ fontSize: 12, color: T.soft, marginTop: 7, lineHeight: 1.7 }}>
            <b style={{ color: T.ink }}>Viewer</b> reads the contracts you assign
            them and changes nothing.
            <br />
            <b style={{ color: T.ink }}>Member</b> reads and edits the contracts you
            assign them.
            <br />
            <b style={{ color: T.ink }}>Admin</b> reads and edits every contract
            without being assigned, and sees the portfolio view.
            <br /><br />
            None of the three can add anybody or permanently delete anything.
            That stays with you.
          </div>
        </Labelled>
        <Notice>
          No email is sent. You will get a temporary password to pass on yourself,
          and they have to replace it before they can reach anything.
        </Notice>
        {error && <Notice kind="error">{error}</Notice>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={send} disabled={busy || !email || !fullName}>
            {busy ? "Creating..." : "Create the account"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

/* A password, shown once.
 *
 * Once is the whole point. It is not stored anywhere this app can read, so
 * closing this panel is the last time anybody sees it, and if it is lost the
 * answer is to issue another rather than to go looking. */
function PasswordModal({ issued, onClose }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(issued.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal title={issued.again ? `New password for ${issued.name}` : `${issued.name} is set up`}
      onClose={onClose}>
      <div style={{ display: "grid", gap: 14 }}>
        <div style={{ fontSize: 13.5, color: T.ink, lineHeight: 1.75 }}>
          Send this to them yourself, along with the address of the app. They sign
          in with it once and are made to choose their own straight away.
        </div>

        <div style={{
          background: T.chip, border: `1px solid ${T.line}`, borderRadius: 4,
          padding: "14px 16px", textAlign: "center",
        }}>
          <div style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
            fontSize: 22, letterSpacing: "0.08em", color: T.navy, fontWeight: 700,
          }}>{issued.password}</div>
          {issued.email && (
            <div style={{ fontSize: 12, color: T.soft, marginTop: 6 }}>for {issued.email}</div>
          )}
        </div>

        <Btn kind="ghost" full onClick={copy}>
          {copied ? "Copied" : "Copy the password"}
        </Btn>

        <Notice kind="error">
          This is the only time it is shown. It is not stored anywhere the app can
          read it back. If it gets lost, issue another one.
        </Notice>

        <Btn full onClick={onClose}>Done</Btn>
      </div>
    </Modal>
  );
}
